"use client";

import { createPath } from "@/service/routing/routing";
import { PictureUpload } from "@/components/common/picture-upload/picture-upload";
import { SuperLink as Link } from "@/components/common/super-link";
import { logEvent } from "@/service/logs/log";
import { useContext } from "react";
import { ResourceContext } from "@/contexts/resource-context";
import { unwrapDateValue, verboseDatetimeFormat } from "@/service/date/article";
import dayjs from "dayjs";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { videoCardBadgeStyles } from "@/components/video/video-card/service";
import { ImageHoverWrapper } from "@/components/common/image-hover-wrapper";

export const PublicationCard = ({
  article: {
    slug,
    image,
    category,
    publishedAt,
    updatedAt,
    title,
    isVademecum,
    isUpdated,
  },
  instantImageLoad = false,
  logClick,
  onClick = () => {},
  aside = false,
  horizontal = false,
  isRead = false,
}: {
  article: ArticlePreviewVM;
  instantImageLoad?: boolean;
  logClick?: "user" | "resource" | undefined;
  onClick?: () => void;
  aside?: boolean;
  horizontal?: boolean;
  isRead?: boolean;
}) => {
  const categorySlug = category.slug;
  const link = createPath(isVademecum ? "vademecum.show" : "publication.show", {
    categorySlug: categorySlug,
    slug: slug,
  });

  const categoryLink = createPath(
    isVademecum ? "vademecum.category" : "publication.category",
    {
      slug: categorySlug,
    },
  );
  const { logView } = useContext(ResourceContext);

  const onLogClick = logClick
    ? () => {
        logEvent("click_recommended_publication", logView, {
          resourceURL: link,
          recommended_type: logClick,
        });
      }
    : () => {};

  const Image = (
    <ImageHoverWrapper
      rounded={cn("@768:rounded-3 rounded-xl", aside && "xl:rounded-lg")}
    >
      <Link
        href={link}
        className={"h-full"}
        onClick={() => {
          onLogClick();
          onClick();
        }}
      >
        <div className={"h-full"}>
          {isRead && (
            <Badge
              className={cn(
                videoCardBadgeStyles,
                "bg-remedium-blue top-3 left-3 z-10 text-white",
              )}
            >
              Czytany
            </Badge>
          )}

          <PictureUpload
            image={image}
            loaded={instantImageLoad}
            className={"h-full object-cover"}
            aspectRatio={"1.91 / 1"}
            type={"card"}
          />
        </div>
      </Link>
    </ImageHoverWrapper>
  );
  const Title = (
    <div
      className={cn(
        `text-foreground text-remedium-blue-hover text-base font-[550]`,
        aside && "xl:text-sm",
      )}
    >
      <Link
        href={link}
        onClick={() => {
          onLogClick();
          onClick();
        }}
      >
        {title}
      </Link>
    </div>
  );
  const Meta = (
    <div className={`flex items-center gap-2 text-sm text-zinc-500`}>
      <div className={"whitespace-nowrap"}>
        <Link
          href={categoryLink}
          className={"text-remedium-blue font-semibold"}
        >
          {category.name}
        </Link>
      </div>
      <div>·</div>
      <div className={"line-clamp-1"}>
        {!isUpdated ? (
          <span
            data-title={publishedAt ? verboseDatetimeFormat(publishedAt) : ""}
          >
            {publishedAt ? verboseDatetimeFormat(publishedAt) : ""}
          </span>
        ) : (
          (() => {
            const updated = unwrapDateValue(updatedAt);
            const label =
              updated && dayjs(updated).isValid()
                ? `aktualizacja - ${dayjs(updated).format("DD.MM.YYYY")}`
                : "";
            return (
              <span data-title={label}>{label}</span>
            );
          })()
        )}
      </div>
    </div>
  );

  if (horizontal) {
    return (
      <>
        <div className={"flex flex-col @min-[600px]:hidden"}>
          <div className={"mb-4"}>{Image}</div>
          <div className={"mb-1.5"}>{Title}</div>
          {Meta}
        </div>
        <div className={"hidden gap-6 @min-[600px]:flex"}>
          <div className={"w-[50%]"}>{Image}</div>
          <div className={"flex w-[50%] min-w-[290px] flex-col justify-center"}>
            <div className={"mb-1.5"}>{Meta}</div>
            <div className={"mb-1.5"}>{Title}</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      className={"flex flex-col"}
      {...(isRead ? { id: "active-read-in-aside" } : {})}
    >
      <div className={cn("mb-4", aside && "xl:mb-2")}> {Image}</div>
      <div className={"mb-1.5"}>{Title}</div>
      {Meta}
    </div>
  );
};
